import moment from 'moment-timezone';
import _ from 'lodash';
import { enums } from 'console-kit';

import RefData from 'ref-data';
import { DisplayUnit } from 'common-entities';
import { OVERLAY_SIZE, FREQUENCY_CAP, DEFAULT_FREQUENCY_CAP_NUM } from '../enums/lightbox-launch-constants';
import { publishDestinationEditorTabs, embedDestinationsModes } from '../enums';
import { objectToParamsString } from '../tools';

//WYN-4303 [V] used in PrimaryExperienceActionHandler.jsx as in ScheduleStep.jsx to create GMT formatted dates.
//In PrimaryExperienceActionHandler.jsx is used to set the seeded, unchangeable date publishDate to let the user know
//"when now is"
const normalizeToGMT = (momentDateTime, timeZone) => {
  const strippedTZMoment = momentDateTime.format('YYYY-MM-DD HH:mm:ss');
  const tzDateTime = moment.tz(strippedTZMoment, timeZone);
  return moment(tzDateTime.tz('GMT'), 'YYYY-MM-DDTHH:mm:ssZ').format();
};

const displayDestinationType = (destinationTypeId) =>
  [RefData.Enum.TYPE.DESTINATION.WEB, RefData.Enum.TYPE.DESTINATION.MICROSITE].includes(destinationTypeId);

const getNextStepBaseOnDisplayUnits = (destinationType, displayUnits, experience, pinnedContainers) => {
  const displayUnitsForDestinationType = DisplayUnit.Collection.filterByFormatIdAndDestinationType(
    { destinationType, experienceFormatTypeId: experience.formatType.id },
    displayUnits
  );
  // Some units have a dimensions step, so they go next, if not - go to the Privacy management
  // Display unit step is show: 1) if there are more than 1 DU, or 2) if there is at least one available pinned container
  const _pinnedContainers = pinnedContainers || [];
  if (_.size(displayUnitsForDestinationType) !== 1 || _.size(_pinnedContainers) > 0) {
    return 4; // goes to the Display Unit Step
  } else {
    return 6; // goes to the Schedule Step
  }
};

/**
 * Returns the step number that should show up after channel selection.
 */
const getChannelNextStep = (destinationType, displayUnits, experience, pinnedContainers) => {
  // Move next step if destinationType is OVERLAY(Lightbox)
  if (destinationType.id === RefData.Enum.TYPE.DESTINATION.OVERLAY) {
    return 2; // goes to the Lightbox Launch Method Step
  }

  if (!displayDestinationType(destinationType.id)) {
    return 6; //goes to the Schedule step
  } else {
    return getNextStepBaseOnDisplayUnits(destinationType, displayUnits, experience, pinnedContainers);
  }
};

// get tabs for editor overlay
const getEditorTabs = (publishingDestination) => {
  const tabItems = [];

  const isWebEmbed = publishingDestination.isWebEmbed || publishingDestination.isWebOverlay;
  const isMicrosite = publishingDestination.isMicrosite;

  if (isWebEmbed) {
    tabItems.push({ text: 'Settings', value: publishDestinationEditorTabs.SETTINGS });
  }
  if (isWebEmbed || isMicrosite) {
    tabItems.push({ text: 'Cookie Consent', value: publishDestinationEditorTabs.PRIVACY });
  }
  tabItems.push({ text: 'Code', value: publishDestinationEditorTabs.CODE });

  return tabItems;
};

// get default tab of editor overlay
const getDefaultEditorTabValue = (publishingDestination) =>
  publishingDestination.isMicrosite ? publishDestinationEditorTabs.CODE : getEditorTabs(publishingDestination)[0].value;

// get web embed params
const getWebEmbedParams = (publishingDestination, isMicroExperience) => {
  const { growable, xDimension, yDimension, displayConfig } = publishingDestination.displayContainer;
  const scrollBar = _.isNil(displayConfig.webEmbed.scrollBar)
    ? !(growable || isMicroExperience)
    : displayConfig.webEmbed.scrollBar;

  return {
    mode: growable
      ? isMicroExperience
        ? embedDestinationsModes.ADAPTIVE
        : embedDestinationsModes.RESPONSIVE
      : embedDestinationsModes.FIXED,
    scrolling: scrollBar ? 'yes' : 'no',
    ...(!growable && {
      width:  xDimension,
      height: yDimension,
    }),
  };
};

// generate web embed <script> with entered params
const generatedWebEmbedCode = (publishingDestination, isMicroExperience, isOpenId) =>
  publishingDestination.displayContainer[isOpenId ? 'scriptTemplateOpenId' : 'scriptTemplate'].replace(
    /{params}/g,
    objectToParamsString(getWebEmbedParams(publishingDestination, isMicroExperience))
  );

// get common styles for button and link settings
const getButtonLinkConfig = _.memoize((isEditorConfig) => ({
  commonHeaderProps: {
    size:  enums.sizes.X3,
    style: { marginBottom: isEditorConfig ? 24 : 12 },
    font:  {
      weight:   isEditorConfig ? enums.font.weights.MEDIUM : enums.font.weights.LIGHT,
      family:   enums.font.families.SYSTEM,
      emphasis: enums.font.emphasis.NORMAL,
    },
  },
}));

// generate lightbox preview src
const generatedPreviewSrc = (isLightbox, displayContainer, cacheBuster) => {
  let params = {};

  if (cacheBuster) {
    params.cb = cacheBuster;
  }

  if (isLightbox) {
    params = {
      mode: OVERLAY_SIZE.ADAPTIVE,
      ...getLightBoxParams(displayContainer),
      ...params,
    };
  }

  return `${displayContainer.embedUrl}?${objectToParamsString(params)}`;
};

// generate lightbox <script> with entered params
const generatedLightboxCode = (publishingDestination, isAdvancedMode, isOpenId) => {
  let script = publishingDestination.displayContainer[isOpenId ? 'scriptTemplateOpenId' : 'scriptTemplate'];

  if (!isAdvancedMode) {
    script = script.replace(/embed/g, 'lightbox');
  }

  const mode = publishingDestination.embedCodeParams.mode;
  const params = { mode, ...getLightBoxParams(publishingDestination.displayContainer) };

  return script.replace(/{params}/g, objectToParamsString(params));
};

const getLightBoxParams = (displayContainer) => {
  const lightboxParams = { ...prepareLightboxConfigToUrl(displayContainer) };
  if (displayContainer.xDimension) lightboxParams.width = displayContainer.xDimension;
  if (displayContainer.yDimension) lightboxParams.height = displayContainer.yDimension;
  return lightboxParams;
};

// convert lightbox config to pass as src params
const prepareLightboxConfigToUrl = (displayContainer) => {
  const lightboxConfig = displayContainer.displayConfig.lightboxConfig;

  const config = {
    ...lightboxConfig,
    ...(lightboxConfig.frequencyCapType &&
      displayContainer.privacyMgmt &&
      displayContainer.privacyMgmt !== RefData.Enum.STRATEGY.PRIVACYMGMT.NONE && { privacyEnabled: true }),
    ...(lightboxConfig.textColor && { textColor: lightboxConfig.textColor.replace('#', '') }),
    ...(lightboxConfig.buttonColor && { buttonColor: lightboxConfig.buttonColor.replace('#', '') }),
  };

  if (
    // default frequency cap num to 1 for ONCE and ONCE_PER_VISIT options since they work in combo with a numerical value that represents the number of views
    [FREQUENCY_CAP.ONCE, FREQUENCY_CAP.ONCE_PER_VISIT].includes(lightboxConfig.frequencyCapType) ||
    // default frequency cap num to 1 for ONCE_PER_X_DAYS option if frequencyCapNum is not set since it should work in combo with a custom numerical value
    (lightboxConfig.frequencyCapType === FREQUENCY_CAP.ONCE_PER_X_DAYS && !lightboxConfig.frequencyCapNum)
  ) {
    config.frequencyCapNum = DEFAULT_FREQUENCY_CAP_NUM;
  }

  // clean up the config object
  if (
    ![FREQUENCY_CAP.ONCE, FREQUENCY_CAP.ONCE_PER_VISIT, FREQUENCY_CAP.ONCE_PER_X_DAYS].includes(
      lightboxConfig.frequencyCapType
    )
  ) {
    delete config.frequencyCapNum;
  }

  return _.pickBy(config, (value) => !_.isNil(value));
};

export {
  normalizeToGMT,
  getNextStepBaseOnDisplayUnits,
  getChannelNextStep,
  getEditorTabs,
  getDefaultEditorTabValue,
  getWebEmbedParams,
  generatedWebEmbedCode,
  getButtonLinkConfig,
  generatedPreviewSrc,
  prepareLightboxConfigToUrl,
  getLightBoxParams,
  generatedLightboxCode,
};
